/**
 * OKRHealthDashboard Component
 * Composition component for the full OKR health report
 *
 * Story 4.6: OKR Health Report Page Assembly
 */

import React from 'react';
import { clsx } from 'clsx';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../common';
import type { DateRange } from '../DateRangeSelector';
import { OKRHealthReportSkeleton } from '../ReportLoadingSkeleton';
import { ReportErrorState } from '../ReportEmptyState';
import { HealthSummaryCard } from './HealthSummaryCard';
import { ProgressDistributionChart } from './ProgressDistributionChart';
import { AtRiskOKRList } from './AtRiskOKRList';
import { StagnantOKRList } from './StagnantOKRList';
import { CompletionPredictionCard } from './CompletionPredictionCard';
import { useOKRHealthReportData } from '../../../hooks/useReportData';
import type { ReportFilterParams } from '../../../services/reportApi';
import { toReportFilterParams, type ReportFilters } from '../ReportFilterPanel';
import { format } from 'date-fns';

export interface OKRHealthDashboardProps {
  /** Date range for the report (controlled by parent) */
  dateRange: DateRange;
  /**
   * Report filters, owned by ReportsPage (D-B3).
   *
   * This dashboard used to keep its OWN `useState<ReportFilters>` and its own
   * filter panel, so the page had two independent filter UIs that disagreed
   * with each other. There is now exactly one, in the page header.
   */
  filters?: ReportFilters;
  /** Stagnant OKR threshold in days */
  stagnantThresholdDays?: number;
  /** Click handler for OKR */
  onOKRClick?: (okrId: string) => void;
  /** Click handler for user/owner */
  onUserClick?: (userId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * OKR Health Dashboard Component
 */
export const OKRHealthDashboard: React.FC<OKRHealthDashboardProps> = ({
  dateRange,
  filters,
  stagnantThresholdDays = 14,
  onOKRClick,
  onUserClick,
  className,
}) => {
  // Empty arrays become "no restriction" at the UI boundary — see toReportFilterParams.
  const apiFilters: ReportFilterParams = toReportFilterParams(filters);

  // Format date range params for API calls
  const dateRangeParams = {
    startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
    endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
  };

  // Fetch health report data
  const healthData = useOKRHealthReportData(dateRangeParams, apiFilters, stagnantThresholdDays);

  // Handle refresh
  const handleRefresh = () => {
    healthData.healthSummary.refetch();
    healthData.progressDistribution.refetch();
    healthData.atRiskOKRs.refetch();
    healthData.stagnantOKRs.refetch();
    healthData.completionPrediction.refetch();
  };

  // Check for loading state (initial load only)
  const isInitialLoading = healthData.isLoading && !healthData.healthSummary.data;

  if (isInitialLoading) {
    return (
      <div className={className}>
        <OKRHealthReportSkeleton />
      </div>
    );
  }

  // Check for error state
  if (healthData.isError) {
    return (
      <div className={className}>
        <ReportErrorState
          error={healthData.error instanceof Error ? healthData.error : new Error('Failed to load health report')}
          onRetry={handleRefresh}
        />
      </div>
    );
  }

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Header with actions.
          The filter button lives in the page header now (D-B3) — this
          dashboard no longer owns a second, independent one. */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900">OKR Health Report</h2>
          <p className="text-stone-600 mt-1">Monitor OKR health and identify issues early</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={healthData.isLoading}
        >
          <RefreshCw className={clsx('w-4 h-4 mr-2', healthData.isLoading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Health Summary */}
      {healthData.healthSummary.data && (
        <HealthSummaryCard
          data={healthData.healthSummary.data}
          isLoading={healthData.healthSummary.isLoading}
          scope={healthData.scopes.healthSummary}
        />
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Progress Distribution - Full width on mobile, left column on desktop */}
        {healthData.progressDistribution.data && (
          <ProgressDistributionChart
            data={healthData.progressDistribution.data}
            isLoading={healthData.progressDistribution.isLoading}
            scope={healthData.scopes.progressDistribution}
            className="lg:col-span-1"
          />
        )}

        {/* Completion Prediction */}
        {healthData.completionPrediction.data && (
          <CompletionPredictionCard
            data={healthData.completionPrediction.data}
            isLoading={healthData.completionPrediction.isLoading}
            className="lg:col-span-1"
          />
        )}
      </div>

      {/* At-Risk and Stagnant OKRs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* At-Risk OKRs */}
        {healthData.atRiskOKRs.data && (
          <AtRiskOKRList
            data={healthData.atRiskOKRs.data}
            isLoading={healthData.atRiskOKRs.isLoading}
            onOKRClick={onOKRClick}
            onOwnerClick={onUserClick}
          />
        )}

        {/* Stagnant OKRs */}
        {healthData.stagnantOKRs.data && (
          <StagnantOKRList
            data={healthData.stagnantOKRs.data}
            isLoading={healthData.stagnantOKRs.isLoading}
            onOKRClick={onOKRClick}
            onOwnerClick={onUserClick}
          />
        )}
      </div>
    </div>
  );
};

export default OKRHealthDashboard;
